/**
 * Article canvas template presets — the FlexibleLayout-only template list the
 * article Flow editor feeds into the shared template panel (AC-003).
 *
 * These are plain V2 StepNode templates: the canvas clones them through
 * v2ScreenAdapter.createFromV2Template (fresh UUID, edges cleared), so the
 * ids below are template identities, not screen ids.
 */

import type { StepNode } from '@segmently/schema-models/v2';

const TEXT_COLOR_PRIMARY = '#172033';
const TEXT_COLOR_SECONDARY = '#394150';
const ARTICLE_BACKGROUND = '#F7F8FA';

function localizedLabel(
  text: string,
  language: string,
  appearance: Record<string, unknown>,
): Record<string, unknown> {
  return {
    translations: { [language]: text },
    kind: 'static',
    appearance,
  };
}

function baseArticleScreen(
  id: string,
  name: string,
  language: string,
  sections: Array<Record<string, unknown>>,
): StepNode {
  return {
    id,
    name,
    tags: ['article'],
    screenType: 'FlexibleLayout',
    position: { x: 0, y: 0 },
    content: {
      canvas: {
        background: { kind: 'solid', content: { color: ARTICLE_BACKGROUND } },
        insets: { top: 0, end: 0, bottom: 0, start: 0 },
        webViewBackgroundColor: ARTICLE_BACKGROUND,
      },
      animated: false,
      offlineFirst: true,
      flexibleLayout: {
        scrollable: true,
        insets: { top: 48, end: 32, bottom: 56, start: 32 },
        sections,
      },
    },
  } as unknown as StepNode;
}

/**
 * FlexibleLayout-only presets for the article template panel.
 *
 * @param language draft default language — seeded into section translations
 */
export function getArticleTemplateScreens(language: string = 'en'): StepNode[] {
  const textSections = [
    {
      id: 'article_headline',
      order: 0,
      kind: 'Text',
      label: 'Headline',
      layout: { heightKind: 'auto', padding: { top: 24, end: 0, bottom: 16, start: 0 } },
      textContent: {
        title: localizedLabel('Section headline', language, {
          align: 'start', fontSize: 30, fontWeight: 800, lineHeight: 36, color: TEXT_COLOR_PRIMARY,
        }),
        subtitle: localizedLabel('Write the narrative of this article screen here.', language, {
          align: 'start', fontSize: 16, fontWeight: 400, lineHeight: 25, color: TEXT_COLOR_SECONDARY,
        }),
      },
    },
  ];

  const buttonSection = {
    id: 'article_cta',
    order: 1,
    kind: 'Button',
    label: 'Continue button',
    layout: { heightKind: 'auto', padding: { top: 16, end: 0, bottom: 8, start: 0 } },
    buttonContent: {
      content: localizedLabel('Continue', language, {
        align: 'center', fontSize: 16, fontWeight: 600, color: '#FFFFFF',
      }),
      action: { kind: 'advance', edges: [] },
    },
  };

  return [
    baseArticleScreen('article_template_text', 'Article screen', language, textSections),
    baseArticleScreen('article_template_text_button', 'Article screen with button', language, [
      ...textSections,
      buttonSection,
    ]),
  ];
}
